import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Handle, Position } from '@xyflow/react'
import { getVariantStyles, getSizeStyles } from './variants'
import NodeContent from './NodeContent'

const HANDLE_STYLE = { width: 6, height: 6, opacity: 0 }

function FlowGraphBanner ({ data, selected }) {
  const {
    nameValue, nameConfig,
    descriptionValue, descriptionConfig,
    image,
    variant = 'primary',
    size = 'md',
    color
  } = data

  const variantStyles = getVariantStyles(variant, color)
  const sizeStyles = getSizeStyles(size)

  const headerStyle = useMemo(() => ({
    background: variantStyles.background,
    color: variantStyles.color,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: sizeStyles.fontSize,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    lineHeight: 1.3
  }), [variantStyles, sizeStyles])

  const bodyStyle = useMemo(() => ({
    background: 'var(--color-surface-050)',
    padding: descriptionValue != null ? '8px 16px' : '0',
    fontSize: '0.8125em',
    color: 'var(--root-color)',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    opacity: 0.85
  }), [descriptionValue])

  const containerStyle = useMemo(() => ({
    width: sizeStyles.width,
    borderRadius: 12,
    border: `2px solid ${selected ? variantStyles.selectedBorderColor : variantStyles.borderColor}`,
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: selected ? '0 0 0 3px rgba(0,0,0,0.08)' : '0 2px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease'
  }), [sizeStyles, variantStyles, selected])

  const handleColor = variantStyles.borderColor

  return (
    <div style={containerStyle}>
      <Handle type='target' position={Position.Top} style={{ ...HANDLE_STYLE, background: handleColor }} />
      <div style={headerStyle}>
        <NodeContent
          nameValue={nameValue}
          nameConfig={nameConfig}
          image={image}
          variantColor={variantStyles.color}
        />
      </div>
      {descriptionValue != null && (
        <div style={bodyStyle}>
          <NodeContent
            descriptionValue={descriptionValue}
            descriptionConfig={descriptionConfig}
          />
        </div>
      )}
      <Handle type='source' position={Position.Bottom} style={{ ...HANDLE_STYLE, background: handleColor }} />
    </div>
  )
}

FlowGraphBanner.propTypes = {
  data: PropTypes.shape({
    nameValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nameConfig: PropTypes.object,
    descriptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionConfig: PropTypes.object,
    image: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.object,
    size: PropTypes.string
  }),
  selected: PropTypes.bool
}

export default memo(FlowGraphBanner)
